import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { sanitizeError, logError } from '../lib/errorHandling';
import { setUserContext, clearUserContext } from '../lib/monitoring';

interface AdminUser {
  id: string;
  email: string;
  fullName: string;
  role: 'super_admin' | 'admin' | 'moderator';
  isActive: boolean;
}

interface AccountLockoutInfo {
  isLocked: boolean;
  lockedUntil?: string;
  lockCount?: number;
  reason?: string;
}

interface AuthContextType {
  user: User | null;
  adminUser: AdminUser | null;
  loading: boolean;
  lockoutInfo: AccountLockoutInfo | null;
  signIn: (email: string, password: string) => Promise<{ error: Error | null; isAdmin: boolean }>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  checkAccountLockout: (email: string) => Promise<AccountLockoutInfo>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

async function hashIP(ip: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(ip);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

async function getClientIP(): Promise<string | null> {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error('Error fetching IP:', error);
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [lockoutInfo, setLockoutInfo] = useState<AccountLockoutInfo | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchAdminUser(session.user.id);
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchAdminUser(session.user.id);
      } else {
        setAdminUser(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchAdminUser = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('*')
        .eq('id', userId)
        .eq('is_active', true)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        const admin = {
          id: data.id,
          email: data.email,
          fullName: data.full_name,
          role: data.role,
          isActive: data.is_active,
        };
        setAdminUser(admin);
        setUserContext({
          id: admin.id,
          email: admin.email,
          role: admin.role,
        });
      } else {
        setAdminUser(null);
        clearUserContext();
      }
    } catch (error) {
      console.error('Error fetching admin user:', error);
      setAdminUser(null);
    } finally {
      setLoading(false);
    }
  };

  const checkAccountLockout = async (email: string): Promise<AccountLockoutInfo> => {
    try {
      const { data, error } = await supabase.rpc('get_account_lockout_info', {
        user_email: email,
      });

      if (error) {
        console.error('[Auth] Error checking lockout:', error);
        return { isLocked: false };
      }

      return data as AccountLockoutInfo;
    } catch (error) {
      console.error('[Auth] Error checking lockout:', error);
      return { isLocked: false };
    }
  };

  const logLoginAttempt = async (email: string, success: boolean, failureReason?: string) => {
    try {
      const rawIP = await getClientIP();
      const ipAddress = rawIP ? await hashIP(rawIP) : null;
      const userAgent = navigator.userAgent;

      await supabase.from('login_attempts').insert({
        email,
        ip_address: ipAddress,
        user_agent: userAgent,
        success,
        failure_reason: failureReason,
        attempt_time: new Date().toISOString(),
      });
    } catch (error) {
      console.error('[Auth] Error logging login attempt:', error);
    }
  };

  const createAccountLockout = async (email: string) => {
    try {
      const { data: recentAttempts } = await supabase.rpc('count_recent_failed_attempts', {
        user_email: email,
        window_minutes: 15,
      });

      const failedCount = recentAttempts || 0;

      const { data: existingLockouts } = await supabase
        .from('account_lockouts')
        .select('lock_count')
        .eq('email', email)
        .order('locked_at', { ascending: false })
        .limit(1);

      const previousLockCount = existingLockouts && existingLockouts.length > 0 ? existingLockouts[0].lock_count : 0;
      const newLockCount = previousLockCount + 1;

      let lockDuration: number;
      if (newLockCount === 1) lockDuration = 15;
      else if (newLockCount === 2) lockDuration = 60;
      else if (newLockCount === 3) lockDuration = 360;
      else lockDuration = 1440;

      const lockedUntil = new Date();
      lockedUntil.setMinutes(lockedUntil.getMinutes() + lockDuration);

      await supabase.from('account_lockouts').insert({
        email,
        locked_until: lockedUntil.toISOString(),
        lock_count: newLockCount,
        reason: `Too many failed login attempts (${failedCount} in 15 minutes)`,
        locked_at: new Date().toISOString(),
      });

      return {
        isLocked: true,
        lockedUntil: lockedUntil.toISOString(),
        lockCount: newLockCount,
        reason: `Account locked due to ${failedCount} failed login attempts`,
      };
    } catch (error) {
      console.error('[Auth] Error creating lockout:', error);
      return { isLocked: false };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      console.log('[Auth] Attempting sign in');

      const lockout = await checkAccountLockout(email);
      setLockoutInfo(lockout);

      if (lockout.isLocked) {
        await logLoginAttempt(email, false, 'Account locked');
        return {
          error: new Error(`Account is locked until ${new Date(lockout.lockedUntil!).toLocaleString()}. ${lockout.reason || ''}`),
          isAdmin: false,
        };
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        logError('[Auth] Sign in error', error);
        const sanitizedError = sanitizeError(error, 'signIn');
        await logLoginAttempt(email, false, sanitizedError);

        const { data: recentFailures } = await supabase.rpc('count_recent_failed_attempts', {
          user_email: email,
          window_minutes: 15,
        });

        const failedCount = (recentFailures || 0) + 1;

        if (failedCount >= 5) {
          const newLockout = await createAccountLockout(email);
          setLockoutInfo(newLockout);
          return {
            error: new Error(`Too many failed login attempts. Account locked until ${new Date(newLockout.lockedUntil!).toLocaleString()}`),
            isAdmin: false,
          };
        }

        throw new Error(sanitizedError);
      }

      console.log('[Auth] Sign in successful');

      if (data.user) {
        console.log('[Auth] Checking admin access');

        const { data: adminData, error: adminError } = await supabase
          .from('admin_users')
          .select('*')
          .eq('id', data.user.id)
          .eq('is_active', true)
          .maybeSingle();

        if (adminError) {
          console.error('[Auth] Error fetching admin user:', adminError);
        }

        if (adminData) {
          console.log('[Auth] Admin access granted');
          await logLoginAttempt(email, true);

          await supabase
            .from('admin_users')
            .update({ last_login_at: new Date().toISOString() })
            .eq('id', data.user.id);

          setLockoutInfo(null);
          setAdminUser({
            id: adminData.id,
            email: adminData.email,
            fullName: adminData.full_name,
            role: adminData.role,
            isActive: adminData.is_active,
          });
          setLoading(false);
          return { error: null, isAdmin: true };
        } else {
          console.warn('[Auth] User authenticated but no admin record found');
          await logLoginAttempt(email, false, 'Not an admin user');
          await supabase.auth.signOut();
          return {
            error: new Error('You do not have admin access. Please contact an administrator.'),
            isAdmin: false
          };
        }
      }

      return { error: null, isAdmin: false };
    } catch (error) {
      console.error('[Auth] Sign in catch block error:', error);
      return { error: error as Error, isAdmin: false };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setAdminUser(null);
    clearUserContext();
  };

  const value = {
    user,
    adminUser,
    loading,
    lockoutInfo,
    signIn,
    signOut,
    isAdmin: !!adminUser,
    checkAccountLockout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
